import clsx from 'clsx';
import { m } from 'framer-motion';
import { cloneElement, ReactElement, useContext, useState } from 'react';
import { DashboardLayoutContext } from './dashboard-layout-context';
import { MenuOpenIcon } from '@ui/icons/material/MenuOpen'; // Ícones de seta
import { MenuIcon } from '@ui/icons/material/Menu'; // Ícones de seta
import { KeyboardArrowLeftIcon } from '@ui/icons/material/KeyboardArrowLeft'; // Ícones de seta

import { ArrowForwardIosIcon } from '@ui/icons/material/ArrowForwardIos'; // Ícones de seta
import { ArrowBackIosIcon } from '@ui/icons/material/ArrowBackIos'; // Ícones de seta


export interface DashboardSidenavChildrenProps {
  className?: string;
  isCompactMode?: boolean;
}

export interface SidenavProps {
  className?: string;
  children: ReactElement<DashboardSidenavChildrenProps>;
  position?: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg' | string;
  mode?: 'overlay';
  overlayPosition?: 'absolute' | 'fixed';
  display?: 'flex' | 'block';
  overflow?: string;
  forceClosed?: boolean;
}

export function DashboardSidenav({
  className,
  position,
  children,
  size = 'md',
  mode,
  overlayPosition = 'fixed',
  display = 'flex',
  overflow = 'overflow-hidden',
  forceClosed = false,
}: SidenavProps) {
  const {
    isMobileMode,
    leftSidenavStatus,
    setLeftSidenavStatus,
    rightSidenavStatus,
    setRightSidenavStatus,
  } = useContext(DashboardLayoutContext);

  const status = position === 'left' ? leftSidenavStatus : rightSidenavStatus;
  const setStatus = position === 'left' ? setLeftSidenavStatus : setRightSidenavStatus;

  const isOverlayMode = isMobileMode || mode === 'overlay';

  // Estado para controlar se a barra está minimizada ou expandida
  const [isMinimized, setIsMinimized] = useState(false);

  const toggleSidebar = () => {
    if (status === 'closed') {
      setStatus('open');
      setIsMinimized(false);
    } else {
      setStatus(isMinimized ? 'open' : 'closed');
      setIsMinimized(!isMinimized);
    }
  };

  const variants = {
    open: { display, width: null as any },
    compact: {
      display,
      width: null as any,
    },
    closed: {
      width: 0,
      transitionEnd: {
        display: 'none',
      },
    },
  };

  const sizeClassName = getSize(status === 'compact' ? 'compact' : size);

  return (
    <>
      {/* Mantém o botão de alternar no lado esquerdo */}
      {position === 'left' && (
        <div
          className={clsx(
            'fixed z-30',
            isMinimized ? 'left-[7px]' : 'left-[calc(15.2rem)]', // Distância da borda esquerda
          )}
          style={{ top: '3.3rem' }} // Ajuste exato para a distância do topo
        >
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-full bg-gray-200 shadow-md hover:bg-gray-300"
          >
            {isMinimized ? <MenuIcon /> : <KeyboardArrowLeftIcon />}
          </button>
        </div>
      )}

      <m.div
        variants={variants}
        initial={false}
        animate={forceClosed ? 'closed' : status}
        transition={{ type: 'tween', duration: 0.15 }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (isMobileMode && (target.closest('button') || target.closest('a'))) {
            setLeftSidenavStatus('closed');
            setRightSidenavStatus('closed');
          }
        }}
        className={clsx(
          className,
          position === 'left'
            ? 'dashboard-grid-sidenav-left'
            : 'dashboard-grid-sidenav-right',
          'will-change-[width]',
          overflow,
          sizeClassName,
          isOverlayMode && `${overlayPosition} bottom-0 top-0 z-20 shadow-2xl`,
          isOverlayMode && position === 'left' && 'left-0',
          isOverlayMode && position === 'right' && 'right-0'
        )}
      >
        {cloneElement<DashboardSidenavChildrenProps>(children, {
          className: clsx(
            children.props.className,
            'w-full h-full',
            status === 'compact' && 'compact-scrollbar'
          ),
          isCompactMode: status === 'compact',
        })}
      </m.div>
    </>
  );
}

function getSize(size: SidenavProps['size'] | 'compact'): string {
  switch (size) {
    case 'compact':
      return 'w-60';
    case 'sm':
      return 'w-224';
    case 'md':
      return 'w-240';
    case 'lg':
      return 'w-288';
    default:
      return size || '';
  }
}
